'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { IconButton } from '@/components/platform/ui'
import { DashboardSidebar } from './DashboardSidebar'
import type { NavSection, RadarTab } from './nav-model'

/**
 * The app frame: navigation rail, top bar, scrollable main.
 *
 * `data-surface='radar-dashboard'` on the root is what activates the platform
 * palette (see globals.css). It is scoped here rather than on <html> so the
 * marketing site and every other route keep the global tokens untouched.
 *
 * Below `lg` the rail becomes a dismissible drawer. Every destination in the
 * nav model is reachable at every breakpoint.
 */
export function DashboardShell({
  navSections,
  activeTab,
  onNavigate,
  navOpen,
  onNavOpenChange,
  topBar,
  progress,
  mainRef,
  children,
}: {
  navSections: NavSection[]
  activeTab: RadarTab
  onNavigate: (tab: RadarTab) => void
  navOpen: boolean
  onNavOpenChange: (open: boolean) => void
  topBar: ReactNode
  /** Global scan progress bar, rendered flush under the top bar. */
  progress?: ReactNode
  /** Scroll container, used for per-tab scroll restoration. */
  mainRef?: React.Ref<HTMLDivElement>
  children: ReactNode
}) {
  const drawerRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  // Escape closes the drawer, and opening it moves focus inside so a keyboard
  // user is not left tabbing through the page behind the overlay.
  useEffect(() => {
    if (!navOpen) return
    closeRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onNavOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navOpen, onNavOpenChange])

  return (
    <div
      data-surface='radar-dashboard'
      className='flex min-h-screen flex-col bg-surface-app text-foreground'
    >
      <div className='flex min-h-0 flex-1'>
        {/* Desktop rail */}
        {/* 232px rail — the radar-v2 arrangement's `--sidebar`. Wide enough
            that "Recommendations" and "All domains" stop wrapping against
            their count badges at the default zoom. */}
        <aside className='hidden w-[232px] flex-shrink-0 border-r border-hairline lg:block'>
          <div className='sticky top-0 h-screen'>
            <DashboardSidebar
              sections={navSections}
              activeTab={activeTab}
              onNavigate={onNavigate}
            />
          </div>
        </aside>

        {/* Mobile drawer */}
        {navOpen && (
          <div className='fixed inset-0 z-50 lg:hidden'>
            <button
              type='button'
              aria-label='Close navigation'
              tabIndex={-1}
              onClick={() => onNavOpenChange(false)}
              className='absolute inset-0 bg-black/70'
            />
            <div
              ref={drawerRef}
              role='dialog'
              aria-modal='true'
              aria-label='Dashboard navigation'
              className='absolute inset-y-0 left-0 flex w-[264px] max-w-[85vw] flex-col border-r border-hairline'
            >
              <div className='absolute right-2 top-3 z-10'>
                <IconButton
                  ref={closeRef}
                  icon={X}
                  aria-label='Close navigation'
                  variant='ghost'
                  size='sm'
                  onClick={() => onNavOpenChange(false)}
                />
              </div>
              <DashboardSidebar
                sections={navSections}
                activeTab={activeTab}
                onNavigate={tab => {
                  onNavigate(tab)
                  onNavOpenChange(false)
                }}
              />
            </div>
          </div>
        )}

        <div className='flex min-w-0 flex-1 flex-col bg-surface-page'>
          {topBar}
          {progress}
          {/* A DIV, not <main>. The root layout (app/layout.tsx) already
              renders <main id='main-content'> around every page, and nesting a
              second main landmark inside it is an ARIA violation — a screen
              reader gets two "main" regions and the skip link becomes
              ambiguous. This element only exists to be the scroll container. */}
          <div ref={mainRef} className='flex-1 overflow-y-auto'>
            {/* ONE content column for every screen on this surface — the
                dashboard, the new-audit view and every tab share it, so
                nothing reflows when the user moves between them.

                Fluid up to 1520px, then centred. Wide enough that the score
                hero, category grid, charts and tables use the desktop
                workspace instead of sitting in a narrow ribbon, but still
                bounded so the page never runs edge-to-edge.

                It is deliberately NOT a readable-measure container: prose that
                would stretch at this width caps itself locally (max-w-[68ch]),
                so text stays legible while data surfaces get the full canvas. */}
            <div className='mx-auto flex w-full max-w-[1520px] flex-col gap-5 px-4 pb-10 pt-6 sm:px-8 sm:pt-7'>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
